/**
 * Состояние сделки (#766, PR2) — накопленная сводка по сделке или лиду после
 * каждого разговора: строка `deal_states` (Postgres, append-only), колонка
 * `state`. Спека: docs/plans/2026-10-03-deal-state.md §4 (модель), §6 (виды
 * следующего действия); план: docs/plans/2026-10-03-deal-state-pr2-step.md
 * (D2–D5, D12, D13).
 *
 * Две схемы, как у «голоса клиента» (`voice-extraction.ts`):
 * - `rawDealStateOutputSchema` — ответ модели: мягкий по форме (длина фразы,
 *   лишний пункт или несуществующая дата не выбрасывают оплаченный ответ — их
 *   режет нормализация шага), строгий по кодам (незнакомый код — не
 *   формулировка, которую можно подрезать);
 * - `dealStateSchema` — хранимое состояние: каждый текст и список с потолком,
 *   коды закрыты, ключи списков уникальны.
 *
 * Чего нет в ответе модели и что ставит код: методика (`methodology` — из
 * настройки воронки), `basedOn`, `previousStateId`, у причины риска —
 * `recordId` и `atSec` (секунда — из диаризации по цитате, как у
 * `voice_extraction`). `relevant` есть ТОЛЬКО в ответе модели: `false` —
 * разговор не про сделку, строки нет вовсе (D6).
 *
 * Отступления от §4 спеки, и почему:
 * - у договорённости есть `id`: открытую договорённость прошлого состояния,
 *   которую модель опустила, код возвращает сам (D5), и ему нужно знать,
 *   какой из пунктов — она. Возражение такого ключа не требует: оно одно на
 *   код из закрытого набора 19 (`reasonKey` уникален);
 * - цитата возражения — `quote`, а не `lastQuote`: `minimizeDerivedOutput`
 *   выбрасывает из полей с ИМЕНЕМ `quote` цитату с неклассифицируемым рядом
 *   цифр (номер договора, карты), а `lastQuote` такой защиты не получил бы;
 * - `schemaVersion` — в самом состоянии, как `schema_version` у
 *   `voice_extraction_v1`: JSON читается и без колонки.
 */
import { z } from 'zod/v4';
// The leaf entry, not the `@aica/core/voc` barrel: the barrel value-imports
// back into api-types and would close the import cycle (see sales-methodology.ts).
import { OBJECTION_CODES } from '@aica/core/voc/objection-codes';
import { salesFrameworkSchema } from './sales-methodology';

/** Версия формы состояния. Новая форма — новая версия, читатель сверяет её. */
export const DEAL_STATE_SCHEMA_VERSION = 'deal_state_v1';

/** Куда идёт сделка — корзины очереди сделок (PR4). */
export const DEAL_STATUSES = ['at_risk', 'needs_attention', 'on_track'] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

/**
 * Следующее действие — закрытый список (§6, по SalesUp): позвонить сегодня,
 * отправить предложение, выставить счёт, согласовать оплату, отработать
 * возражение, реактивировать, подождать ответа, не тратить время. Закрытый,
 * потому что по нему строятся очередь и фильтры.
 */
export const NEXT_ACTION_KINDS = [
  'call_today',
  'send_proposal',
  'issue_invoice',
  'agree_payment',
  'handle_objection',
  'reactivate',
  'await_reply',
  'deprioritize',
] as const;
export type NextActionKind = (typeof NEXT_ACTION_KINDS)[number];

/** `overdue` — тоже открытая: закрывает договорённость только `done`. */
export const COMMITMENT_STATUSES = ['open', 'done', 'overdue'] as const;
export type CommitmentStatus = (typeof COMMITMENT_STATUSES)[number];

export const OBJECTION_STATUSES = ['open', 'resolved'] as const;
export type ObjectionStatus = (typeof OBJECTION_STATUSES)[number];

export const DEAL_INTEREST_LEVELS = ['high', 'medium', 'low'] as const;
export type DealInterestLevel = (typeof DEAL_INTEREST_LEVELS)[number];

/** Сущность CRM, у которой есть состояние (R11: сделки и лиды сразу). */
export const DEAL_ENTITY_TYPES = ['deal', 'lead'] as const;
export type DealEntityType = (typeof DEAL_ENTITY_TYPES)[number];

/**
 * Откуда время разговора в строке (`conversation_at`, D4): `version_ts`
 * записи — время самого разговора в источнике; `created_at` — его нет, и
 * взято время, когда запись появилась у нас.
 */
export const CONVERSATION_TIME_SOURCES = ['version_ts', 'created_at'] as const;
export type ConversationTimeSource = (typeof CONVERSATION_TIME_SOURCES)[number];

/**
 * Комментарий о состоянии в таймлайне CRM (#766, PR3, план
 * docs/plans/2026-10-04-deal-state-pr3-crm-comment.md, V3) — колонка
 * `deal_states.crm_comment_status`; `NULL` — ещё не решено. `publishing` —
 * заявка взята (условный UPDATE из `NULL` или просроченной заявки), остальные
 * — итог.
 */
export const DEAL_STATE_COMMENT_STATUSES = [
  'publishing',
  'published',
  'suppressed_late',
  'skipped',
  'failed',
] as const;
export type DealStateCommentStatus = (typeof DEAL_STATE_COMMENT_STATUSES)[number];

/** Итоговые статусы — после них комментарий этой строки больше не публикуется. */
export type DealStateCommentFinalStatus = Exclude<DealStateCommentStatus, 'publishing'>;

/**
 * Задача в CRM по состоянию сделки (#766, PR4a, план
 * docs/plans/2026-10-04-deal-state-pr4-deal-queue.md, D6–D7) — колонка
 * `deal_states.crm_task_status`; `NULL` — задачи не было. `creating` — заявка
 * взята (условный UPDATE из `NULL`, `failed` или просроченной заявки),
 * `created` — CRM создала задачу, `failed` — отказала, повтор разрешён.
 */
export const DEAL_STATE_TASK_STATUSES = ['creating', 'created', 'failed'] as const;
export type DealStateTaskStatus = (typeof DEAL_STATE_TASK_STATUSES)[number];

/** Потолки хранимого состояния — нормализация шага режет по тем же числам. */
export const DEAL_STATE_LIMITS = {
  closeChanceMin: 0,
  closeChanceMax: 100,
  /** «Что дал этот разговор» — 1–3 пункта (§4). */
  changedSinceLastMax: 3,
  /** План на следующий контакт — 2–3 пункта (§4). */
  talkPlanMax: 3,
  /** Один пункт списка: изменение, пункт плана. */
  pointMaxChars: 200,
  riskTextMaxChars: 300,
  whyMaxChars: 300,
  /**
   * Цитата — ПДн и живёт дольше транскрипта (R10); длиннее одной реплики ей
   * быть незачем — тот же потолок, что у `voice_extraction`.
   */
  quoteMaxChars: 300,
  /**
   * Договорённостей в состоянии. Открытые накапливаются (D5), поэтому потолок
   * с запасом; при переполнении первыми уходят выполненные
   * (`mergeCarriedItems`).
   */
  commitmentsMax: 50,
  commitmentIdMaxChars: 64,
  whoMaxChars: 80,
  whatMaxChars: 200,
  /** По одному на код: больше, чем кодов, не бывает. */
  objectionsMax: OBJECTION_CODES.length,
  factMaxChars: 200,
  /** Id записи или состояния — строка нашей базы, не текст модели. */
  idMaxChars: 64,
} as const;

const L = DEAL_STATE_LIMITS;

const dealStatusSchema = z.enum(DEAL_STATUSES);
const nextActionKindSchema = z.enum(NEXT_ACTION_KINDS);
const commitmentStatusSchema = z.enum(COMMITMENT_STATUSES);
const objectionStatusSchema = z.enum(OBJECTION_STATUSES);
const interestSchema = z.enum(DEAL_INTEREST_LEVELS);
const objectionCodeSchema = z.enum(OBJECTION_CODES);

const idSchema = z.string().min(1).max(L.idMaxChars);
const pointSchema = z.string().min(1).max(L.pointMaxChars);
const quoteSchema = z.string().max(L.quoteMaxChars);
/** Срок — календарная дата `YYYY-MM-DD`; «на следующей неделе» нормализация отбрасывает. */
const dueBySchema = z.iso.date().optional();

export const dealRiskReasonSchema = z.object({
  text: z.string().min(1).max(L.riskTextMaxChars),
  /**
   * Дословная цитата — текст транскрипта этого разговора, а не строка модели
   * (D9); пустая — изъята редакцией. Имя поля — `quote`: его редактирует
   * `minimizeDerivedOutput`.
   */
  quote: quoteSchema.optional(),
  /** Разговор, из которого цитата. */
  recordId: idSchema.optional(),
  /** Секунда разговора — у звонков, из диаризации по цитате. */
  atSec: z.number().int().min(0).optional(),
});
export type DealRiskReason = z.infer<typeof dealRiskReasonSchema>;

export const dealNextActionSchema = z.object({
  kind: nextActionKindSchema,
  dueBy: dueBySchema,
  why: z.string().min(1).max(L.whyMaxChars),
});
export type DealNextAction = z.infer<typeof dealNextActionSchema>;

export const dealCommitmentSchema = z.object({
  /** Ключ договорённости в цепочке: по нему код узнаёт её в следующем состоянии (D5). */
  id: z
    .string()
    .min(1)
    .max(L.commitmentIdMaxChars)
    .regex(/^[A-Za-z0-9_-]+$/),
  who: z.string().min(1).max(L.whoMaxChars),
  what: z.string().min(1).max(L.whatMaxChars),
  dueBy: dueBySchema,
  status: commitmentStatusSchema,
  /** Разговор, в котором договорённость появилась или последний раз сменила статус. */
  sourceRecordId: idSchema,
});
export type DealCommitment = z.infer<typeof dealCommitmentSchema>;

export const dealObjectionSchema = z.object({
  reasonKey: objectionCodeSchema,
  status: objectionStatusSchema,
  /** Последняя цитата по возражению; имя — `quote`, см. заголовок файла. */
  quote: quoteSchema.optional(),
  /** Разговор, в котором возражение прозвучало или последний раз сменило статус. */
  sourceRecordId: idSchema,
});
export type DealObjection = z.infer<typeof dealObjectionSchema>;

const qualificationItemSchema = z.object({
  known: z.boolean(),
  /** Краткий факт: что известно о потребности, бюджете, ЛПР, сроках. */
  fact: z.string().min(1).max(L.factMaxChars).optional(),
});

export const dealQualificationSchema = z.object({
  need: qualificationItemSchema,
  budget: qualificationItemSchema,
  authority: qualificationItemSchema,
  timeline: qualificationItemSchema,
});
export type DealQualification = z.infer<typeof dealQualificationSchema>;

export const dealMethodologySchema = z.object({
  framework: salesFrameworkSchema,
  /** Были ли у воронки правила компании на момент пересчёта (§7, слой 2). */
  companyRules: z.boolean(),
});
export type DealMethodology = z.infer<typeof dealMethodologySchema>;

/**
 * Хранимое состояние — колонка `deal_states.state`. Писатель — только
 * `DealStateRepository.append`, который разбирает его этой схемой на границе
 * хранилища и берёт из него колонки строки (версия, методика, разговор,
 * предыдущее состояние), чтобы JSON и колонки не разошлись.
 */
export const dealStateSchema = z.object({
  schemaVersion: z.literal(DEAL_STATE_SCHEMA_VERSION),
  status: dealStatusSchema,
  closeChance: z.number().int().min(L.closeChanceMin).max(L.closeChanceMax),
  changedSinceLast: z.array(pointSchema).max(L.changedSinceLastMax),
  riskReason: dealRiskReasonSchema.nullable(),
  nextAction: dealNextActionSchema,
  talkPlan: z.array(pointSchema).max(L.talkPlanMax),
  commitments: z
    .array(dealCommitmentSchema)
    .max(L.commitmentsMax)
    .refine((items) => new Set(items.map((c) => c.id)).size === items.length, {
      message: 'commitment ids are unique within a state',
    }),
  objections: z
    .array(dealObjectionSchema)
    .max(L.objectionsMax)
    .refine((items) => new Set(items.map((o) => o.reasonKey)).size === items.length, {
      message: 'one objection per reason code',
    }),
  qualification: dealQualificationSchema,
  interest: interestSchema,
  methodology: dealMethodologySchema,
  /** Разговор, после которого посчитано, и его время. */
  basedOn: z.object({
    recordId: idSchema,
    at: z.iso.datetime({ offset: true }),
  }),
  /**
   * Голова цепочки, на которой посчитано; `null` — первое состояние сделки,
   * и у сделки из лида тоже (#1813): ссылка не уходит за ключ сделки, иначе
   * `uq_deal_states_first` не держал бы её первую строку.
   */
  previousStateId: z.guid().nullable(),
  /**
   * Лид, из которого сконвертирована сделка (#1813) — только для сервера
   * (очередь), не для модели и не для зрителя: см. `withoutLeadLink`.
   * `entityId` — `LEAD_ID` сделки из CRM, пишется в каждое состояние, когда
   * CRM его назвала (иначе переносится из головы): очередь убирает этот лид
   * из корзин. `stateId` — состояние лида, на котором началась цепочка
   * сделки; нет — сделка начата с нуля (лида без состояний или CRM не
   * ответила при первом разговоре).
   */
  continuedFrom: z
    .object({
      entityType: z.literal('lead'),
      entityId: idSchema,
      stateId: z.guid().optional(),
    })
    .optional(),
});
export type DealState = z.infer<typeof dealStateSchema>;

/**
 * Состояние без связи с лидом (#1813) — для всего, что уходит из сервера:
 * промпт модели (privacy A.3: id лида модели не передаётся) и ответ зрителю
 * (он может не видеть лида). Одно место, чтобы новый путь не забыл.
 */
export function withoutLeadLink<T extends { continuedFrom?: unknown }>(
  state: T
): Omit<T, 'continuedFrom'> {
  const { continuedFrom: _lead, ...rest } = state;
  return rest;
}

const rawQualificationItemSchema = z.object({
  known: z.boolean(),
  fact: z.string().nullable().optional(),
});

/**
 * Сырой ответ модели — до нормализации. Строгая JSON-схема шага держит
 * перечни и обязательность полей; здесь — только ФОРМА, и мягко: длина
 * фразы, лишний пункт сверх потолка, шанс вне 0..100 или срок не датой не
 * должны выбрасывать уже оплаченный ответ — их подрезает нормализация (тот же
 * урок, что у `rawVoiceExtractionOutputSchema`). Коды остаются строгими.
 *
 * `null` и отсутствие поля равноценны там, где поле необязательно: строгая
 * JSON-схема провайдера просит `null`, а не пропуск.
 */
export const rawDealStateOutputSchema = z.object({
  /** `false` — разговор не про эту сделку: состояние не пересчитывается (D6). */
  relevant: z.boolean(),
  status: dealStatusSchema,
  closeChance: z.number(),
  changedSinceLast: z.array(z.string()),
  riskReason: z
    .object({
      text: z.string(),
      quote: z.string().nullable().optional(),
      /**
       * Запись, на которой стоит причина (#1832): этого разговора — причина
       * из него; `recordId` причины прошлого состояния — та же причина
       * перенесена, этот разговор её не повторил (тогда `quote: null`).
       */
      sourceRecordId: z.string().nullable().optional(),
    })
    .nullable(),
  nextAction: z.object({
    kind: nextActionKindSchema,
    dueBy: z.string().nullable().optional(),
    why: z.string(),
  }),
  talkPlan: z.array(z.string()),
  commitments: z.array(
    z.object({
      /** Id договорённости прошлого состояния; `null` — новая. */
      id: z.string().nullable().optional(),
      who: z.string(),
      what: z.string(),
      dueBy: z.string().nullable().optional(),
      status: commitmentStatusSchema,
      sourceRecordId: z.string(),
    })
  ),
  objections: z.array(
    z.object({
      reasonKey: objectionCodeSchema,
      status: objectionStatusSchema,
      quote: z.string().nullable().optional(),
      sourceRecordId: z.string(),
    })
  ),
  qualification: z.object({
    need: rawQualificationItemSchema,
    budget: rawQualificationItemSchema,
    authority: rawQualificationItemSchema,
    timeline: rawQualificationItemSchema,
  }),
  interest: interestSchema,
});
export type RawDealStateOutput = z.infer<typeof rawDealStateOutputSchema>;

/** Закрыта ли договорённость: `overdue` — открытая, просто просроченная. */
const isOpenCommitment = (item: DealCommitment): boolean => item.status !== 'done';
const isOpenObjection = (item: DealObjection): boolean => item.status === 'open';

/**
 * Детерминированное сохранение накопленного (D5): защита от «сводки сводки».
 *
 * - Открытая договорённость (`open`/`overdue`) или открытое возражение
 *   головы, которых нет в `next`, возвращаются как были — со своим статусом,
 *   после пунктов `next`.
 * - Закрыть открытый пункт головы (`done`/`resolved`) `next` может только с
 *   опорой на этот разговор: `sourceRecordId === recordId`. Иначе пункт
 *   возвращается таким, каким был в голове, — модель не вправе снять
 *   возражение по памяти о прошлых разговорах.
 * - Пункты, которых в голове не было, и пункты, закрытые ещё в голове, —
 *   как их дал `next`: это нормализация шага, а не сохранение.
 *
 * Договорённость узнаётся по `id`, возражение — по `reasonKey`. Сверх
 * `commitmentsMax` первыми уходят выполненные (с начала списка); если
 * переполнен список одних открытых — хвост возвращённых (на практике 50 открытых
 * договорённостей у одной сделки не бывает, но схема хранения обязана
 * выполняться всегда).
 *
 * Чистая: ни `head`, ни `next` не меняются.
 */
export function mergeCarriedItems(
  head: Pick<DealState, 'commitments' | 'objections'> | null,
  next: DealState,
  recordId: string
): DealState {
  if (!head) return next;

  const headCommitments = new Map(head.commitments.map((c) => [c.id, c]));
  const nextCommitmentIds = new Set(next.commitments.map((c) => c.id));
  const commitments = next.commitments.map((item) => {
    const before = headCommitments.get(item.id);
    const closesWithoutThisConversation =
      before !== undefined &&
      isOpenCommitment(before) &&
      !isOpenCommitment(item) &&
      item.sourceRecordId !== recordId;
    return closesWithoutThisConversation ? before : item;
  });
  for (const before of head.commitments) {
    if (isOpenCommitment(before) && !nextCommitmentIds.has(before.id)) {
      commitments.push(before);
    }
  }

  const headObjections = new Map(head.objections.map((o) => [o.reasonKey, o]));
  const nextObjectionKeys = new Set(next.objections.map((o) => o.reasonKey));
  const objections = next.objections.map((item) => {
    const before = headObjections.get(item.reasonKey);
    const resolvesWithoutThisConversation =
      before !== undefined &&
      isOpenObjection(before) &&
      !isOpenObjection(item) &&
      item.sourceRecordId !== recordId;
    return resolvesWithoutThisConversation ? before : item;
  });
  for (const before of head.objections) {
    if (isOpenObjection(before) && !nextObjectionKeys.has(before.reasonKey)) {
      objections.push(before);
    }
  }

  return {
    ...next,
    commitments: capCommitments(commitments),
    objections,
  };
}

/** Сверх потолка: сначала выполненные с начала списка, потом хвост. */
function capCommitments(items: DealCommitment[]): DealCommitment[] {
  let excess = items.length - L.commitmentsMax;
  if (excess <= 0) return items;
  const kept = items.filter((item) => {
    if (excess > 0 && !isOpenCommitment(item)) {
      excess -= 1;
      return false;
    }
    return true;
  });
  return kept.slice(0, L.commitmentsMax);
}
